import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { SecurityBlock, SecurityBlockQuery } from "@nh/shared";
import { Alert, Badge, Button, Input, Select, type BadgeVariant } from "@/shared/ui/core";
import { ConfirmDialog, EmptyState, SkeletonTable } from "@/shared/ui/ui";
import {
  DataTable,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { liftBlock, listBlocks } from "./audit.api";
import { formatAuditDateTime } from "./format";

/** Las rutas que caben en la celda; el resto se cuenta. */
const VISIBLE_PATHS = 3;

type BlockState = "vigente" | "vencido" | "levantado";

function blockState(block: SecurityBlock): BlockState {
  if (block.liftedAt) return "levantado";
  return block.active ? "vigente" : "vencido";
}

const STATE_LABELS: Record<BlockState, string> = {
  vigente: "Vigente",
  vencido: "Vencido",
  levantado: "Levantado",
};

const STATE_VARIANTS: Record<BlockState, BadgeVariant> = {
  vigente: "error",
  vencido: "neutral",
  levantado: "info",
};

/**
 * Lista negra — las IP que el servidor vetó por portarse como escáner.
 * Un veto vigente se puede LEVANTAR antes de tiempo (un cliente real que
 * cayó en la red), siempre con una confirmación en la página.
 */
export function AuditBlocksTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "expired">("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [pending, setPending] = useState<SecurityBlock | null>(null);
  const [liftError, setLiftError] = useState<string | null>(null);

  const query: SecurityBlockQuery = {
    page,
    perPage,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(status ? { status } : {}),
  };

  const blocks = useQuery({
    queryKey: ["admin", "audit", "blocks", query],
    queryFn: () => listBlocks(query),
    placeholderData: (previous) => previous,
  });

  const levantar = useMutation({
    mutationFn: (block: SecurityBlock) => liftBlock(block.id),
    onSuccess: () => {
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit", "blocks"] });
    },
    onError: (cause) => {
      setPending(null);
      setLiftError(cause instanceof Error ? cause.message : t("No se pudo levantar el veto"));
    },
  });

  const items = blocks.data?.items ?? [];
  const total = blocks.data?.total ?? 0;
  const filtered = search.trim() !== "" || status !== "";

  return (
    <div aria-busy={blocks.isFetching}>
      <FilterBar>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Buscar")}
          <div className="w-56">
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={t("IP…")}
              aria-label={t("Buscar IP")}
            />
          </div>
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Estado")}
          <div className="w-40">
            <Select
              aria-label={t("Estado del veto")}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as typeof status);
                setPage(1);
              }}
              placeholder={t("Todos")}
              options={[
                { value: "active", label: t("Vigentes") },
                { value: "expired", label: t("Vencidos") },
              ]}
            />
          </div>
        </label>
      </FilterBar>

      {liftError && (
        <Alert variant="error" className="m-4" onDismiss={() => setLiftError(null)}>
          {liftError}
        </Alert>
      )}

      {blocks.isError ? (
        <Alert variant="error" className="m-4">
          {t("No se pudo cargar la lista negra. Inténtalo otra vez.")}
        </Alert>
      ) : blocks.isPending ? (
        <div className="p-5">
          <SkeletonTable rows={5} columns={6} />
        </div>
      ) : items.length === 0 ? (
        <div className="p-4">
          <EmptyState
            title={filtered ? t("Sin resultados") : t("Ninguna IP vetada")}
            description={
              filtered
                ? t("Ninguna IP coincide con esos filtros.")
                : t("Cuando el servidor vete una IP por escanear, aparece aquí.")
            }
          />
        </div>
      ) : (
        <DataTable caption={t("IP en la lista negra")}>
          <thead>
            <tr>
              <Th className="w-[16%]">{t("IP")}</Th>
              <Th className="w-[15%] whitespace-nowrap">{t("Cuándo")}</Th>
              <Th className="w-[15%] whitespace-nowrap">{t("Hasta cuándo")}</Th>
              <Th className="w-[15%]">{t("Estado")}</Th>
              <Th>{t("Rutas probadas")}</Th>
              <Th align="right" className="w-[12%]">
                <span className="sr-only">{t("Acciones")}</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {items.map((block) => {
              const state = blockState(block);
              const extra = block.paths.length - VISIBLE_PATHS;
              return (
                <Tr key={block.id}>
                  <Td className="whitespace-nowrap font-mono text-[13px]">{block.ip}</Td>
                  <Td className="whitespace-nowrap text-[13px] text-ds-ink-3">
                    {formatAuditDateTime(block.at)}
                  </Td>
                  <Td className="whitespace-nowrap text-[13px] text-ds-ink-3">
                    {formatAuditDateTime(block.liftedAt ?? block.blockedUntil)}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant={STATE_VARIANTS[state]} size="sm">
                        {t(STATE_LABELS[state])}
                      </Badge>
                      {block.repeat && (
                        <Badge variant="warning" size="sm">
                          {t("Reincidente")}
                        </Badge>
                      )}
                      {block.targeted && (
                        <Badge variant="indigo" size="sm">
                          {t("Dirigido")}
                        </Badge>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <ul className="grid gap-0.5">
                      {block.paths.slice(0, VISIBLE_PATHS).map((path) => (
                        <li key={path} className="break-all font-mono text-[12px] text-ds-ink-3">
                          {path}
                        </li>
                      ))}
                    </ul>
                    {extra > 0 && (
                      <p
                        className="text-[12px] text-ds-ink-3"
                        title={block.paths.slice(VISIBLE_PATHS).join("\n")}
                      >
                        {t("y {{count}} más", { count: extra })}
                      </p>
                    )}
                  </Td>
                  <Td align="right">
                    {state === "vigente" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPending(block)}
                        aria-label={t("Levantar el veto de {{ip}}", { ip: block.ip })}
                      >
                        {t("Levantar veto")}
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </DataTable>
      )}

      {total > 0 && (
        <Pagination
          page={page}
          perPage={perPage}
          total={total}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}

      <ConfirmDialog
        open={pending !== null}
        variant="warning"
        title={t("Levantar el veto")}
        description={
          pending
            ? t("La IP {{ip}} vuelve a poder entrar de inmediato. Hazlo solo si es alguien de verdad.", {
                ip: pending.ip,
              })
            : ""
        }
        confirmLabel={t("Levantar veto")}
        cancelLabel={t("Cancelar")}
        loading={levantar.isPending}
        onConfirm={() => {
          if (pending) levantar.mutate(pending);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
